import { KeyRound, Link2, Lock } from "lucide-react";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { Badge } from "@/components/ui/badge";

import type { ColumnInfo, TableDetail } from "../api";

/**
 * The "Columns" tab: the table's structure — every column with its type, key
 * role, nullability, default and comment — plus a compact index list.
 */

const columns: DataTableColumn<ColumnInfo>[] = [
  {
    key: "name",
    header: "Column",
    render: (c) => (
      <span className="flex items-center gap-1.5">
        <span className="font-mono text-sm">{c.name}</span>
        {c.sensitive ? (
          <Lock className="h-3 w-3 text-muted-foreground" aria-label="masked in data preview" />
        ) : null}
      </span>
    ),
  },
  {
    key: "data_type",
    header: "Type",
    render: (c) => <span className="font-mono text-xs text-muted-foreground">{c.data_type}</span>,
  },
  {
    key: "keys",
    header: "Key",
    render: (c) => (
      <span className="flex flex-wrap items-center gap-1">
        {c.is_primary_key ? (
          <Badge variant="warning" className="gap-1">
            <KeyRound className="h-3 w-3" />
            PK
          </Badge>
        ) : null}
        {c.foreign_key ? (
          <Badge variant="info" className="gap-1" title={c.foreign_key}>
            <Link2 className="h-3 w-3" />
            FK
          </Badge>
        ) : null}
      </span>
    ),
  },
  {
    key: "nullable",
    header: "Nullable",
    align: "center",
    render: (c) =>
      c.nullable ? (
        <span className="text-muted-foreground">yes</span>
      ) : (
        <Badge variant="neutral">NOT NULL</Badge>
      ),
  },
  {
    key: "default",
    header: "Default",
    render: (c) =>
      c.default ? (
        <code className="rounded bg-muted px-1 py-0.5 text-xs">{c.default}</code>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: "comment",
    header: "Comment",
    render: (c) =>
      c.comment ? (
        <span className="text-sm">{c.comment}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
];

export function TableColumnsView({ detail }: { detail: TableDetail }) {
  return (
    <div className="space-y-4">
      <DataTable
        columns={columns}
        rows={detail.columns}
        rowKey={(c) => c.name}
        containerClassName="max-h-[calc(100vh-24rem)]"
      />

      {detail.indexes.length > 0 ? (
        <div className="space-y-1.5">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Indexes
          </h3>
          <ul className="space-y-1">
            {detail.indexes.map((idx) => (
              <li key={idx.name} className="flex items-center gap-2 text-sm">
                <span className="font-mono text-xs">{idx.name}</span>
                <span className="text-muted-foreground">
                  ({idx.columns.join(", ")})
                </span>
                {idx.unique ? <Badge variant="success">unique</Badge> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
